import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import './ListsConfiguration.css';

const API_URL = '/api/admin/lists-configuration/';

const ListsConfiguration = () => {
    const { hasPermission: rawHasPermission, permissions } = usePermissions();
    const hasPermission = (module, action) => {
        if (module === 'LISTS CONFIGURATION' || module === 'ADMIN PANEL LISTS CONFIGURATION') {
            return rawHasPermission('LISTS CONFIGURATION', action) || rawHasPermission('ADMIN PANEL LISTS CONFIGURATION', action);
        }
        return rawHasPermission(module, action);
    };
    const [modules, setModules] = useState([]);
    const [departments, setDepartments] = useState([]);
    // Separate dept selections for doc-type and vendor-type sections
    const [selectedDocDeptId, setSelectedDocDeptId] = useState(null);
    const [selectedVendorDeptId, setSelectedVendorDeptId] = useState(null);
    // Which dept IDs have items in each scoped section (from API)
    const [docTypeDeptIds, setDocTypeDeptIds] = useState(new Set());
    const [vendorTypeDeptIds, setVendorTypeDeptIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newValues, setNewValues] = useState({});
    const [editState, setEditState] = useState({});
    // deptMap: null when closed, {value, section, loading, checkedIds: Set<number>} when open
    const [deptMap, setDeptMap] = useState(null);
    // checkedItems: { [listKey]: Set<string> }
    const [checkedItems, setCheckedItems] = useState({});
    // dept tab orders (drag to reorder), persisted to localStorage
    const [docDeptOrder, setDocDeptOrder] = useState(() => {
        try { return JSON.parse(localStorage.getItem('lists_doc_dept_order') || '[]'); } catch { return []; }
    });
    const [vendorDeptOrder, setVendorDeptOrder] = useState(() => {
        try { return JSON.parse(localStorage.getItem('lists_vendor_dept_order') || '[]'); } catch { return []; }
    });
    // Refs to always have latest dept IDs in fetchModules without stale closure
    const docDeptRef = useRef(null);
    const vendorDeptRef = useRef(null);
    const dragTabRef = useRef(null);

    const canCreate = hasPermission('LISTS CONFIGURATION', 'can_create');
    const canEdit = hasPermission('LISTS CONFIGURATION', 'can_edit');
    const canDelete = hasPermission('LISTS CONFIGURATION', 'can_delete');

    const fetchModules = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (docDeptRef.current) params.set('department_id', docDeptRef.current);
            if (vendorDeptRef.current) params.set('vendor_department_id', vendorDeptRef.current);
            const url = params.toString() ? `${API_URL}?${params.toString()}` : API_URL;
            const res = await fetch(url, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load lists.');
            const data = await res.json();
            setModules(data.modules || []);
            setDocTypeDeptIds(new Set(data.doc_type_dept_ids || []));
            setVendorTypeDeptIds(new Set(data.vendor_type_dept_ids || []));
            if (data.departments && data.departments.length > 0) {
                setDepartments(prev => prev.length === 0 ? data.departments : prev);
            }
        } catch (error) {
            toast.error(error.message || 'Failed to load lists.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Sync deptOrder when new departments arrive from the API
    useEffect(() => {
        if (departments.length === 0) return;
        const sync = (prevOrder, storageKey) => {
            const existingSet = new Set(prevOrder);
            const missing = departments.filter(d => !existingSet.has(d.id)).map(d => d.id);
            if (missing.length === 0) return prevOrder;
            const next = [
                ...prevOrder.filter(id => departments.some(d => d.id === id)),
                ...missing,
            ];
            localStorage.setItem(storageKey, JSON.stringify(next));
            return next;
        };
        setDocDeptOrder(prev => sync(prev, 'lists_doc_dept_order'));
        setVendorDeptOrder(prev => sync(prev, 'lists_vendor_dept_order'));
    }, [departments]);

    // On mount: default document types to All Departments
    useEffect(() => {
        docDeptRef.current = null;
        setSelectedDocDeptId(null);
        fetchModules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDocDeptChange = useCallback((deptId) => {
        docDeptRef.current = deptId;
        setSelectedDocDeptId(deptId);
        setDeptMap(null);
        setCheckedItems({});
        fetchModules();
    }, [fetchModules]);

    const handleVendorDeptChange = useCallback((deptId) => {
        vendorDeptRef.current = deptId;
        setSelectedVendorDeptId(deptId);
        setDeptMap(null);
        setCheckedItems({});
        fetchModules();
    }, [fetchModules]);

    const handleTabDragStart = useCallback((e, deptId) => {
        dragTabRef.current = deptId;
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleTabDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleTabDrop = useCallback((e, targetId, section) => {
        e.preventDefault();
        const fromId = dragTabRef.current;
        if (!fromId || fromId === targetId) return;
        const isVendor = section === 'vendor_types';
        const storageKey = isVendor ? 'lists_vendor_dept_order' : 'lists_doc_dept_order';
        const setter = isVendor ? setVendorDeptOrder : setDocDeptOrder;
        setter(prev => {
            const order = [...prev];
            const fromIdx = order.indexOf(fromId);
            const toIdx = order.indexOf(targetId);
            if (fromIdx === -1 || toIdx === -1) return prev;
            order.splice(fromIdx, 1);
            order.splice(toIdx, 0, fromId);
            localStorage.setItem(storageKey, JSON.stringify(order));
            return order;
        });
        dragTabRef.current = null;
    }, []);

    const toggleItem = useCallback((listKey, value) => {
        setCheckedItems(prev => {
            const set = new Set(prev[listKey] || []);
            if (set.has(value)) set.delete(value); else set.add(value);
            return { ...prev, [listKey]: new Set(set) };
        });
    }, []);

    const toggleAllItems = useCallback((listKey, items) => {
        setCheckedItems(prev => {
            const current = prev[listKey] || new Set();
            const allSelected = items.length > 0 && items.every(v => current.has(v));
            return { ...prev, [listKey]: allSelected ? new Set() : new Set(items) };
        });
    }, []);

    const handleBulkDelete = useCallback(async (listKey, isDeptScoped, activeDeptId) => {
        const selected = checkedItems[listKey];
        if (!selected || selected.size === 0) return;
        const count = selected.size;
        const msg = isDeptScoped && activeDeptId
            ? `Remove ${count} selected item(s) from this department's list?`
            : `Delete ${count} selected item(s) from this list?`;
        if (!window.confirm(msg)) return;

        setSaving(true);
        try {
            const results = await Promise.allSettled(
                Array.from(selected).map(value => {
                    const body = { list_key: listKey, value };
                    if (isDeptScoped && activeDeptId) body.department_id = activeDeptId;
                    return fetch(API_URL, {
                        method: 'DELETE',
                        headers: getAuthHeaders(),
                        body: JSON.stringify(body),
                    }).then(async res => {
                        if (!res.ok) {
                            const err = await res.json().catch(() => ({}));
                            throw new Error(err.detail || `Failed to delete "${value}".`);
                        }
                    });
                })
            );
            const failed = results.filter(r => r.status === 'rejected');
            if (failed.length > 0) {
                failed.forEach(f => toast.error(f.reason.message));
            } else {
                toast.success(`${count} item(s) deleted.`);
            }
            setCheckedItems(prev => ({ ...prev, [listKey]: new Set() }));
            await fetchModules();
        } catch (error) {
            toast.error(error.message || 'Bulk delete failed.');
        } finally {
            setSaving(false);
        }
    }, [checkedItems, fetchModules]);

    const openDeptMap = useCallback(async (value, section) => {
        setDeptMap({ value, section, loading: true, checkedIds: new Set() });
        try {
            const param = section === 'vendor_types' ? 'vendor_type_name' : 'doc_type_name';
            const res = await fetch(`${API_URL}?${param}=${encodeURIComponent(value)}`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to load department mappings.');
            const data = await res.json();
            setDeptMap({ value, section, loading: false, checkedIds: new Set(data.mapped_department_ids || []) });
        } catch (error) {
            toast.error(error.message || 'Failed to load department mappings.');
            setDeptMap(null);
        }
    }, []);

    const closeDeptMap = useCallback(() => setDeptMap(null), []);

    const toggleDeptMapId = useCallback((deptId) => {
        setDeptMap(prev => {
            if (!prev) return prev;
            const next = new Set(prev.checkedIds);
            if (next.has(deptId)) next.delete(deptId); else next.add(deptId);
            return { ...prev, checkedIds: next };
        });
    }, []);

    const handleSaveDeptMap = useCallback(async (listKey, value) => {
        if (!deptMap) return;
        setSaving(true);
        try {
            const res = await fetch(API_URL, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    list_key: listKey,
                    value,
                    department_ids: Array.from(deptMap.checkedIds),
                }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to save department mappings.');
            }
            closeDeptMap();
            // Update local state directly instead of refreshing all data
            await fetchModules(); // Need to refresh for department-scoped lists as they may have complex changes
            toast.success('Department mappings updated.');
        } catch (error) {
            toast.error(error.message || 'Failed to save department mappings.');
        } finally {
            setSaving(false);
        }
    }, [deptMap, fetchModules, closeDeptMap]);

    // Departments sorted by user-defined drag order, filtered to those with items in each section
    const sortedDocDepts = useMemo(() => {
        const filtered = departments.filter(d => docTypeDeptIds.has(d.id));
        if (docDeptOrder.length === 0) return filtered;
        const map = Object.fromEntries(filtered.map(d => [d.id, d]));
        const ordered = docDeptOrder.filter(id => map[id]).map(id => map[id]);
        const remaining = filtered.filter(d => !docDeptOrder.includes(d.id));
        return [...ordered, ...remaining];
    }, [departments, docTypeDeptIds, docDeptOrder]);

    const sortedVendorDepts = useMemo(() => {
        const filtered = departments.filter(d => vendorTypeDeptIds.has(d.id));
        if (vendorDeptOrder.length === 0) return filtered;
        const map = Object.fromEntries(filtered.map(d => [d.id, d]));
        const ordered = vendorDeptOrder.filter(id => map[id]).map(id => map[id]);
        const remaining = filtered.filter(d => !vendorDeptOrder.includes(d.id));
        return [...ordered, ...remaining];
    }, [departments, vendorTypeDeptIds, vendorDeptOrder]);

    // Split lists into doc-type, vendor-type, and global sections
    const { docTypeLists, vendorTypeLists, globalLists, totalCount } = useMemo(() => {
        const listMap = {};
        modules.forEach(module => {
            module.lists?.forEach(list => {
                if (!listMap[list.key]) {
                    listMap[list.key] = { ...list, moduleLabel: module.module };
                }
            });
        });

        const docScopedByName = {};
        const vendorScopedByName = {};
        const globalByKey = {};

        Object.values(listMap).forEach(list => {
            if (list.department_scoped && list.section === 'vendor_types') {
                if (!vendorScopedByName[list.name]) {
                    vendorScopedByName[list.name] = { ...list, displayTitle: list.name };
                }
            } else if (list.department_scoped) {
                if (!docScopedByName[list.name]) {
                    docScopedByName[list.name] = { ...list, displayTitle: list.name };
                }
            } else {
                globalByKey[list.key] = list;
            }
        });

        const globalValues = Object.values(globalByKey);
        const globalWithTitles = globalValues.map(list => {
            const sameNameCount = globalValues.filter(l => l.name === list.name).length;
            return {
                ...list,
                displayTitle: sameNameCount > 1 ? `${list.name} (${list.moduleLabel})` : list.name,
            };
        });

        const docList = Object.values(docScopedByName).sort((a, b) => a.displayTitle.localeCompare(b.displayTitle));
        const vendorList = Object.values(vendorScopedByName).sort((a, b) => a.displayTitle.localeCompare(b.displayTitle));
        const globList = globalWithTitles.sort((a, b) => a.displayTitle.localeCompare(b.displayTitle));

        return {
            docTypeLists: docList,
            vendorTypeLists: vendorList,
            globalLists: globList,
            totalCount: docList.length + vendorList.length + globList.length,
        };
    }, [modules]);

    // -- CRUD helpers ----------------------------------------------------------

    const handleAdd = async (listKey, isDeptScoped, activeDeptId) => {
        const value = (newValues[listKey] || '').trim();
        if (!value) { toast.error('Enter a value before adding.'); return; }

        // Warn if value already exists in the currently visible items for this list
        let existingItems = [];
        modules.forEach(module => {
            module.lists?.forEach(list => {
                if (list.key === listKey) existingItems = list.items || [];
            });
        });
        if (existingItems.some(item => item.toUpperCase() === value.toUpperCase())) {
            toast.error(`"${value}" already exists in this list.`);
            return;
        }

        // Backend expects uppercase values
        const body = { list_key: listKey, value: value.toUpperCase() };
        if (isDeptScoped && activeDeptId) body.department_id = activeDeptId;

        setSaving(true);
        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to add value.');
            }
            setNewValues(prev => ({ ...prev, [listKey]: '' }));
            // Update local state directly instead of refreshing all data
            setModules(prevModules => {
                return prevModules.map(module => ({
                    ...module,
                    lists: module.lists?.map(list => {
                        if (list.key === listKey) {
                            return {
                                ...list,
                                items: [...list.items, value.toUpperCase()]
                            };
                        }
                        return list;
                    })
                }));
            });
            toast.success('Value added.');
        } catch (error) {
            toast.error(error.message || 'Failed to add value.');
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (listKey, value) => {
        const upperValue = value.toUpperCase();
        setEditState(prev => ({ ...prev, [`${listKey}:${upperValue}`]: upperValue }));
    };

    const cancelEdit = (listKey, value) => {
        const upperValue = value.toUpperCase();
        setEditState(prev => {
            const next = { ...prev };
            delete next[`${listKey}:${upperValue}`];
            return next;
        });
    };

    const handleSaveEdit = async (listKey, oldValue, isDeptScoped, activeDeptId) => {
        const oldValueUpper = oldValue.toUpperCase();
        const stateKey = `${listKey}:${oldValueUpper}`;
        const newValue = (editState[stateKey] || '').trim();
        if (!newValue) { toast.error('Value cannot be empty.'); return; }

        // Ensure values are uppercase for backend
        const newValueUpper = newValue.toUpperCase();

        // Check if new value already exists in the list (case-insensitive)
        let existingItems = [];
        modules.forEach(module => {
            module.lists?.forEach(list => {
                if (list.key === listKey) existingItems = list.items || [];
            });
        });
        
        // Allow the same value (no change) or check for duplicates with other items
        if (newValueUpper !== oldValueUpper && 
            existingItems.some(item => item.toUpperCase() === newValueUpper)) {
            toast.error(`"${newValue}" already exists in this list.`);
            return;
        }

        // Backend expects uppercase values
        const body = { list_key: listKey, old_value: oldValueUpper, new_value: newValueUpper };
        if (isDeptScoped && activeDeptId) body.department_id = activeDeptId;

        setSaving(true);
        try {
            const res = await fetch(API_URL, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update value.');
            }
            // Update local state directly instead of refreshing all data
            setModules(prevModules => {
                return prevModules.map(module => ({
                    ...module,
                    lists: module.lists?.map(list => {
                        if (list.key === listKey) {
                            return {
                                ...list,
                                items: list.items.map(item => 
                                    item.toUpperCase() === oldValueUpper ? newValueUpper : item
                                )
                            };
                        }
                        return list;
                    })
                }));
            });
            cancelEdit(listKey, oldValue);
            toast.success('Value updated.');
        } catch (error) {
            toast.error(error.message || 'Failed to update value.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (listKey, value, isDeptScoped, activeDeptId) => {
        const msg = isDeptScoped && activeDeptId
            ? `Remove "${value}" from this department's list? (The value stays in the system and other departments are unaffected.)`
            : isDeptScoped && !activeDeptId
            ? `Delete "${value}" from all departments? This will remove it from the system entirely.`
            : `Delete "${value}" from this list?`;
        if (!window.confirm(msg)) return;

        const body = { list_key: listKey, value: value.toUpperCase() };
        if (isDeptScoped && activeDeptId) body.department_id = activeDeptId;

        setSaving(true);
        try {
            const res = await fetch(API_URL, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to remove value.');
            }
            // Update local state directly instead of refreshing all data
            setModules(prevModules => {
                return prevModules.map(module => ({
                    ...module,
                    lists: module.lists?.map(list => {
                        if (list.key === listKey) {
                            return {
                                ...list,
                                items: list.items.filter(item => item.toUpperCase() !== value.toUpperCase())
                            };
                        }
                        return list;
                    })
                }));
            });
            toast.success(isDeptScoped && activeDeptId ? 'Value removed from this department.' : 'Value deleted.');
        } catch (error) {
            toast.error(error.message || 'Failed to remove value.');
        } finally {
            setSaving(false);
        }
    };

    // -- List card renderer ----------------------------------------------------

    const [searchValues, setSearchValues] = React.useState({});
    const handleSearchChange = (key, value) => {
        setSearchValues(prev => ({ ...prev, [key]: value }));
    };

    const renderListCard = (list, activeDeptId) => {
        const isDeptScoped = !!list.department_scoped;
        const section = list.section || 'global';
        const checked = checkedItems[list.key] || new Set();
        const allChecked = list.items.length > 0 && list.items.every(v => checked.has(v));
        const someChecked = !allChecked && list.items.some(v => checked.has(v));
        const colCount = (canDelete ? 1 : 0) + 1 + ((canEdit || canDelete) ? 1 : 0);

        const search = searchValues[list.key] || '';
        const filteredItems = search
            ? list.items.filter(v => v.toLowerCase().includes(search.toLowerCase()))
            : list.items;

        return (
            <div key={list.key} className="list-card">
                <div className="list-card-header">
                    <h3>{list.displayTitle}</h3>
                    <span className="chip">{list.items.length}</span>
                </div>

                {canCreate && (
                    <div className="list-add-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            type="text"
                            value={searchValues[list.key] || ''}
                            onChange={e => handleSearchChange(list.key, e.target.value)}
                            placeholder="Search..."
                            style={{ width: 120, fontSize: 13, textTransform: 'uppercase' }}
                            disabled={saving}
                        />
                        <input
                            type="text"
                            value={newValues[list.key] || ''}
                            onChange={e => setNewValues(prev => ({ ...prev, [list.key]: e.target.value }))}
                            placeholder={`Add ${list.displayTitle}`}
                            style={{ width: 120, fontSize: 13, textTransform: 'uppercase' }}
                            disabled={saving}
                        />
                        <button
                            type="button"
                            onClick={() => handleAdd(list.key, isDeptScoped, activeDeptId)}
                            disabled={saving}
                            style={{ padding: '4px 10px', fontSize: 13, minWidth: 0 }}
                        >
                            {isDeptScoped && activeDeptId ? 'Add to Dept' : 'Add'}
                        </button>
                    </div>
                )}

                <div className="value-table-wrap">
                    <table className="value-table">
                        <thead>
                            <tr>
                                {canDelete && (
                                    <th className="checkbox-col">
                                        <input
                                            type="checkbox"
                                            title={allChecked ? 'Deselect all' : 'Select all'}
                                            checked={allChecked}
                                            ref={el => { if (el) el.indeterminate = someChecked; }}
                                            onChange={() => toggleAllItems(list.key, filteredItems)}
                                            disabled={saving || filteredItems.length === 0}
                                        />
                                    </th>
                                )}
                                <th>Value</th>
                                {(canEdit || canDelete) && (
                                    <th style={{ width: isDeptScoped && canEdit ? 120 : 100 }} className="action-header">Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.length === 0 ? (
                                <tr>
                                    <td colSpan={colCount} className="empty-cell">
                                        {search ? 'No matching values.' : 'No values configured.'}
                                    </td>
                                </tr>
                            ) : filteredItems.map(value => {
                                const upperValue = value.toUpperCase();
                                const stateKey = `${list.key}:${upperValue}`;
                                const isEditing = Object.prototype.hasOwnProperty.call(editState, stateKey);
                                const isDeptMapOpen = deptMap?.value === value && deptMap?.section === section && !isEditing;
                                return (
                                    <React.Fragment key={`${list.key}-${value}`}>
                                        <tr className={checked.has(value) ? 'row-checked' : ''}>
                                            {canDelete && (
                                                <td className="checkbox-cell">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked.has(value)}
                                                        onChange={() => toggleItem(list.key, value)}
                                                        disabled={saving}
                                                    />
                                                </td>
                                            )}
                                            <td style={{ textTransform: 'uppercase' }}>
                                                {isEditing ? (
                                                    <input
                                                        className="edit-input"
                                                        value={editState[stateKey]}
                                                        onChange={e => setEditState(prev => ({ ...prev, [stateKey]: e.target.value }))}
                                                        style={{ textTransform: 'uppercase' }}
                                                    />
                                                ) : value}
                                            </td>
                                            {(canEdit || canDelete) && (
                                                <td className="action-cell">
                                                    {isEditing ? (
                                                        <>
                                                            <button type="button" className="btn-primary" onClick={() => handleSaveEdit(list.key, value, isDeptScoped, activeDeptId)} disabled={saving}>Save</button>
                                                            <button type="button" className="btn-muted" onClick={() => cancelEdit(list.key, value)} disabled={saving}>Cancel</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {canEdit && (
                                                                <button type="button" className="btn-icon" title="Edit" onClick={() => startEdit(list.key, value)} disabled={saving}>
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                            )}
                                                            {canEdit && isDeptScoped && (
                                                                <button
                                                                    type="button"
                                                                    className={`btn-icon${isDeptMapOpen ? ' btn-icon-active' : ''}`}
                                                                    title="Manage departments"
                                                                    onClick={() => isDeptMapOpen ? closeDeptMap() : openDeptMap(value, section)}
                                                                    disabled={saving}
                                                                >
                                                                    <i className="fa fa-share-alt"></i>
                                                                </button>
                                                            )}
                                                            {canDelete && isDeptScoped && activeDeptId && (
                                                                <button type="button" className="btn-icon btn-icon-delete" title="Remove from department" onClick={() => handleDelete(list.key, value, isDeptScoped, activeDeptId)} disabled={saving}>
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            )}
                                                            {canDelete && isDeptScoped && !activeDeptId && (
                                                                <button type="button" className="btn-icon btn-icon-delete" title="Delete from all departments" onClick={() => handleDelete(list.key, value, isDeptScoped, null)} disabled={saving}>
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            )}
                                                            {canDelete && !isDeptScoped && (
                                                                <button type="button" className="btn-icon btn-icon-delete" title="Delete" onClick={() => handleDelete(list.key, value, false, null)} disabled={saving}>
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                        {isDeptMapOpen && (
                                            <tr className="dept-map-tr">
                                                <td colSpan={colCount}>
                                                    <div className="dept-map-panel">
                                                        {deptMap.loading ? (
                                                            <span className="dept-map-loading">Loading mappings�</span>
                                                        ) : (
                                                            <>
                                                                <span className="dept-map-label">Assign <strong>{value}</strong> to departments:</span>
                                                                <div className="dept-map-checkboxes">
                                                                    {departments.map(dept => (
                                                                        <label key={dept.id} className="dept-map-checkbox-label">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={deptMap.checkedIds.has(dept.id)}
                                                                                onChange={() => toggleDeptMapId(dept.id)}
                                                                                disabled={saving}
                                                                            />
                                                                            {dept.name}
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                                <div className="dept-map-actions">
                                                                    <button type="button" className="btn-primary" onClick={() => handleSaveDeptMap(list.key, value)} disabled={saving}>Save</button>
                                                                    <button type="button" className="btn-muted" onClick={closeDeptMap} disabled={saving}>Cancel</button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {checked.size > 0 && canDelete && (
                    <div className="bulk-delete-bar">
                        <button
                            type="button"
                            className="btn-bulk-delete"
                            onClick={() => handleBulkDelete(list.key, isDeptScoped, activeDeptId)}
                            disabled={saving}
                        >
                            <i className="fa fa-trash"></i> Delete selected ({checked.size})
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderDeptTabs = (selectedDeptId, sortedDepts, onDeptChange, section) => (
        <div className="dept-tabs">
            <button
                type="button"
                className={`dept-tab${selectedDeptId === null ? ' dept-tab--active' : ''}`}
                onClick={() => onDeptChange(null)}
            >
                All Departments
            </button>
            {sortedDepts.map(dept => (
                <button
                    key={dept.id}
                    type="button"
                    draggable
                    className={`dept-tab dept-tab--draggable${selectedDeptId === dept.id ? ' dept-tab--active' : ''}`}
                    onClick={() => onDeptChange(dept.id)}
                    onDragStart={e => handleTabDragStart(e, dept.id)}
                    onDragOver={handleTabDragOver}
                    onDrop={e => handleTabDrop(e, dept.id, section)}
                >
                    {dept.name}
                </button>
            ))}
        </div>
    );

    // -- Render ----------------------------------------------------------------

    return (
        <div className="lists-config-container">
            <div className="lists-config-header">
                <h1>Lists Configuration</h1>
                <p>Manage reusable dropdown list values used across modules. Changes apply immediately to list options.</p>
                <div className="lists-config-stats">{totalCount} lists total</div>
            </div>

            {loading ? (
                <div className="lists-loading">Loading configuration lists...</div>
            ) : (
                <>
                    {/* -- Document Types section -- */}
                    {docTypeLists.length > 0 && (
                        <div className="lists-section">
                            <div className="lists-section-header">
                                <h2 className="lists-section-title">Document Types</h2>
                                <span className="lists-section-subtitle">Scoped per department � each department manages its own document types.</span>
                            </div>
                            {renderDeptTabs(selectedDocDeptId, sortedDocDepts, handleDocDeptChange, 'doc_types')}
                            {selectedDocDeptId === null && (
                                <p className="lists-section-hint">
                                    Viewing all document types. Select a department to filter, or use the <i className="fa fa-share-alt"></i> icon on a type to assign it to departments.
                                </p>
                            )}
                            <div className="lists-flat-container">
                                {docTypeLists.map(list => renderListCard(list, selectedDocDeptId))}
                            </div>
                        </div>
                    )}

                    {/* -- Vendor Types section -- */}
                    {vendorTypeLists.length > 0 && (
                        <div className="lists-section">
                            <div className="lists-section-header">
                                <h2 className="lists-section-title">Vendor Types</h2>
                                <span className="lists-section-subtitle">Scoped per department � configure which vendor types are available per department.</span>
                            </div>
                            {renderDeptTabs(selectedVendorDeptId, sortedVendorDepts, handleVendorDeptChange, 'vendor_types')}
                            {selectedVendorDeptId === null && (
                                <p className="lists-section-hint">
                                    Viewing all vendor types. Select a department to filter, or use the <i className="fa fa-share-alt"></i> icon on a type to assign it to departments.
                                </p>
                            )}
                            <div className="lists-flat-container">
                                {vendorTypeLists.map(list => renderListCard(list, selectedVendorDeptId))}
                            </div>
                        </div>
                    )}

                    {/* -- Global lists -- */}
                    {globalLists.length > 0 && (
                        <div className="lists-section">
                            <div className="lists-section-header">
                                <h2 className="lists-section-title">Global Lists</h2>
                                <span className="lists-section-subtitle">Shared across all departments.</span>
                            </div>
                            <div className="lists-flat-container">
                                {globalLists.map(list => renderListCard(list, null))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ListsConfiguration;
